/* json_schema_validator.js */

/**
 * @module json_schema_validator
 *
 * Thin Ajv wrapper used by the JSON editor (and any other consumer) to:
 *  - parse a JSON string and surface syntax errors with a {line,column} pair
 *  - run any number of compiled Ajv schemas against the parsed value (or against
 *    sub-trees identified by JSON pointer) and return errors normalized to
 *    {line,column,message,jsonPointer} suitable for rendering as Ace gutter
 *    annotations.
 *
 * Schemas are compiled once and cached by schema-object identity. Callers
 * pass an array of `{schema, pointer}` entries — `pointer` is an optional
 * JSON pointer (RFC-6901) into the parsed document; when omitted the schema
 * is applied to the root.
 */
const Ajv = require("ajv");
const _ = require("lodash");

const _ajvInstance = new Ajv({allErrors: true, strict: false, allowUnionTypes: true});
const _compiledSchemaCache = new WeakMap();

const _compileSchema = (schema) => {
    if (_compiledSchemaCache.has(schema)) {
        return _compiledSchemaCache.get(schema);
    }
    const compiledValidator = _ajvInstance.compile(schema);
    _compiledSchemaCache.set(schema, compiledValidator);
    return compiledValidator;
};

/**
 * Parse `text` as JSON. Returns either {ok:true, value} or
 * {ok:false, errors:[{line,column,message,jsonPointer:""}]}.
 *
 * Line/column are 0-based to match Ace's annotation API.
 */
const parseJson = (text) => {
    if (text === null || text === undefined || text === "") {
        return {ok: true, value: undefined};
    }
    try {
        return {ok: true, value: JSON.parse(text)};
    } catch (parseError) {
        const message = parseError && parseError.message ? parseError.message : "Invalid JSON";
        // JSON.parse error-message formats vary by engine:
        //   - Older V8 / JSC:        "Unexpected token } in JSON at position 42"
        //   - Newer V8 (≥ 21 / Chrome 120+): often omits the "position N" entirely
        //     but includes "(line N column M)" or the position embedded in a
        //     different sentence. Modern messages also wrap quoted JSON
        //     fragments that look like position markers.
        //
        // Try the explicit "line N column M" form first because it doesn't
        // require us to walk the text, then fall back to "position N".
        const lineColMatch = /line\s+(\d+)[^\d]+column\s+(\d+)/i.exec(message);
        const positionMatch = /position\s+(\d+)/i.exec(message);
        let line = 0;
        let column = 0;
        if (lineColMatch) {
            // JS engines report line/column as 1-based; Ace wants 0-based.
            line = Math.max(parseInt(lineColMatch[1], 10) - 1, 0);
            column = Math.max(parseInt(lineColMatch[2], 10) - 1, 0);
        } else if (positionMatch) {
            const errorPosition = parseInt(positionMatch[1], 10);
            const textBeforeError = text.slice(0, errorPosition);
            const lastNewlineIndex = textBeforeError.lastIndexOf("\n");
            line = (textBeforeError.match(/\n/g) || []).length;
            column = errorPosition - (lastNewlineIndex + 1);
        }
        return {ok: false, errors: [{line, column, message, jsonPointer: ""}]};
    }
};

const _splitJsonPointer = (pointer) =>
    pointer
        .replace(/^\//, "")
        .split("/")
        .map((segment) => segment.replace(/~1/g, "/").replace(/~0/g, "~"));

const _resolvePointer = (rootValue, pointer) => {
    if (!pointer) {
        return rootValue;
    }
    const pointerSegments = _splitJsonPointer(pointer);
    let currentValue = rootValue;
    for (const segment of pointerSegments) {
        if (currentValue === null || currentValue === undefined) {
            return undefined;
        }
        currentValue = currentValue[segment];
    }
    return currentValue;
};

/**
 * For a given JSON pointer find the {line,column} in `text` where the
 * pointed-to value begins. Heuristic: walks the parsed JSON in lockstep with
 * the source text and tracks positions of object keys / array indices.
 *
 * On failure it falls back to {line:0,column:0} so the annotation still shows
 * up at the top of the document rather than being dropped silently.
 */
const pointerToLineColumn = (text, pointer) => {
    if (!pointer) {
        return {line: 0, column: 0};
    }
    const pointerSegments = _splitJsonPointer(pointer);

    let cursor = 0;
    const skipWhitespace = () => {
        while (cursor < text.length && /[\s\r\n]/.test(text[cursor])) {
            cursor++;
        }
    };
    const readQuotedString = () => {
        // assumes text[cursor] === '"'
        const startIndex = ++cursor;
        while (cursor < text.length) {
            if (text[cursor] === "\\") {
                cursor += 2;
                continue;
            }
            if (text[cursor] === '"') {
                break;
            }
            cursor++;
        }
        const stringValue = text.slice(startIndex, cursor);
        cursor++; // past closing quote
        return stringValue;
    };
    const skipJsonValue = () => {
        skipWhitespace();
        const firstChar = text[cursor];
        if (firstChar === '"') {
            readQuotedString();
            return;
        }
        if (firstChar === "{" || firstChar === "[") {
            const openChar = firstChar;
            const closeChar = firstChar === "{" ? "}" : "]";
            let nestingDepth = 0;
            while (cursor < text.length) {
                if (text[cursor] === '"') {
                    readQuotedString();
                    continue;
                }
                if (text[cursor] === openChar) {
                    nestingDepth++;
                } else if (text[cursor] === closeChar) {
                    nestingDepth--;
                    cursor++;
                    if (nestingDepth === 0) {
                        return;
                    }
                    continue;
                }
                cursor++;
            }
            return;
        }
        // Primitive — read until separator or newline.
        while (cursor < text.length && !",}]\n\r".includes(text[cursor])) {
            cursor++;
        }
    };
    const descendIntoSegment = (segment) => {
        skipWhitespace();
        if (text[cursor] === "{") {
            cursor++;
            while (cursor < text.length) {
                skipWhitespace();
                if (text[cursor] === "}") {
                    cursor++;
                    return false;
                }
                if (text[cursor] === ",") {
                    cursor++;
                    continue;
                }
                if (text[cursor] !== '"') {
                    cursor++;
                    continue;
                }
                const key = readQuotedString();
                skipWhitespace();
                if (text[cursor] === ":") {
                    cursor++;
                }
                skipWhitespace();
                if (key === segment) {
                    return true;
                }
                skipJsonValue();
            }
            return false;
        } else if (text[cursor] === "[") {
            cursor++;
            const targetIndex = parseInt(segment, 10);
            let currentIndex = 0;
            while (cursor < text.length) {
                skipWhitespace();
                if (text[cursor] === "]") {
                    cursor++;
                    return false;
                }
                if (text[cursor] === ",") {
                    cursor++;
                    continue;
                }
                if (currentIndex === targetIndex) {
                    return true;
                }
                skipJsonValue();
                currentIndex++;
            }
            return false;
        }
        return false;
    };

    for (const segment of pointerSegments) {
        if (!descendIntoSegment(segment)) {
            return {line: 0, column: 0};
        }
    }
    skipWhitespace();
    const textBeforeCursor = text.slice(0, cursor);
    const line = (textBeforeCursor.match(/\n/g) || []).length;
    const lastNewlineIndex = textBeforeCursor.lastIndexOf("\n");
    const column = cursor - (lastNewlineIndex + 1);
    return {line, column};
};

/**
 * Validate a parsed value against a list of schema entries.
 *
 * @param text {string} The original JSON text (used to resolve pointer→line).
 * @param parsedValue {*} The parsed JSON value.
 * @param schemaEntries {Array<{schema:object, pointer?:string, label?:string}>}
 * @returns {Array<{line,column,message,jsonPointer}>}
 */
const validate = (text, parsedValue, schemaEntries) => {
    if (!schemaEntries || !schemaEntries.length || parsedValue === undefined) {
        return [];
    }
    const validationErrors = [];
    _.each(schemaEntries, (schemaEntry) => {
        const targetValue = _resolvePointer(parsedValue, schemaEntry.pointer);
        if (targetValue === undefined) {
            return;
        }
        const compiledValidator = _compileSchema(schemaEntry.schema);
        const isValid = compiledValidator(targetValue);
        if (isValid) {
            return;
        }
        _.each(compiledValidator.errors, (ajvError) => {
            const fullJsonPointer = (schemaEntry.pointer || "") + (ajvError.instancePath || "");
            const {line, column} = pointerToLineColumn(text, fullJsonPointer);
            const labelPrefix = schemaEntry.label ? `[${schemaEntry.label}] ` : "";
            const allowedValuesSuffix =
                ajvError.params && ajvError.params.allowedValues
                    ? " (" + ajvError.params.allowedValues.join(", ") + ")"
                    : "";
            validationErrors.push({
                line,
                column,
                message: `${labelPrefix}${
                    ajvError.message || "schema violation"
                }${allowedValuesSuffix}`,
                jsonPointer: fullJsonPointer,
            });
        });
    });
    return validationErrors;
};

module.exports = {
    parseJson,
    validate,
    pointerToLineColumn,
};
